import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ItsOverLanding = () => {
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [language, setLanguage] = useState('en');
  const audioRef = useRef(null);
  const { toast } = useToast();

  // Detect browser language and check if email already provided
  useEffect(() => {
    const browserLang = navigator.language.slice(0, 2);
    const supportedLangs = ['en', 'es', 'pt', 'de', 'fr', 'it'];
    setLanguage(supportedLangs.includes(browserLang) ? browserLang : 'en');
    
    // Check if user already provided email in any landing page
    const savedEmail = localStorage.getItem('syntetik_user_email');
    if (savedEmail) {
      setEmailSubmitted(true);
      setEmail(savedEmail);
    }
  }, []);

  const trackInfo = {
    id: '4',
    title: 'Its Over',
    artist: 'SYNTETIK',
    album: 'SYNTETIK',
    year: '2025',
    duration: '3:58',
    audioUrl: 'https://0xx2mevl0on6cu8y.public.blob.vercel-storage.com/Its%20Over%20Lyrics.MP3',
    artwork: 'https://customer-assets.emergentagent.com/job_synthpop-hub/artifacts/5zx9gcj4_YouTube%20header.png'
  };

  const messages = {
    en: {
      unlockTitle: "🎵 Stream Full Song",
      unlockDesc: "Enter your email to unlock high-quality streaming + get notified of new SYNTETIK releases",
      buttonText: "🎵 UNLOCK FULL SONG",
      unlockedTitle: "✅ Full Access Unlocked!",
      unlockedDesc: "Enjoy streaming + discover more tracks",
      moreMusic: "🎵 Discover More SYNTETIK Music",
      visitSite: "Visit SYNTETIK World",
      premium: "✨ Instant access • No spam • Premium quality"
    },
    es: {
      unlockTitle: "🎵 Escucha la Canción Completa",
      unlockDesc: "Ingresa tu email para desbloquear streaming de alta calidad + recibe notificaciones de nuevos lanzamientos SYNTETIK",
      buttonText: "🎵 DESBLOQUEAR CANCIÓN",
      unlockedTitle: "✅ ¡Acceso Completo Desbloqueado!",
      unlockedDesc: "Disfruta del streaming + descubre más canciones",
      moreMusic: "🎵 Descubre Más Música SYNTETIK",
      visitSite: "Visita SYNTETIK World",
      premium: "✨ Acceso instantáneo • Sin spam • Calidad premium"
    },
    pt: {
      unlockTitle: "🎵 Ouça a Música Completa",
      unlockDesc: "Digite seu email para desbloquear streaming de alta qualidade + receba notificações dos novos lançamentos SYNTETIK",
      buttonText: "🎵 DESBLOQUEAR MÚSICA",
      unlockedTitle: "✅ Acesso Completo Desbloqueado!",
      unlockedDesc: "Aproveite o streaming + descubra mais faixas",
      moreMusic: "🎵 Descubra Mais Música SYNTETIK",
      visitSite: "Visite SYNTETIK World",
      premium: "✨ Acesso instantâneo • Sem spam • Qualidade premium"
    },
    de: {
      unlockTitle: "🎵 Vollständigen Song streamen",
      unlockDesc: "E-Mail eingeben für hochwertiges Streaming + Benachrichtigungen über neue SYNTETIK Veröffentlichungen",
      buttonText: "🎵 SONG FREISCHALTEN",
      unlockedTitle: "✅ Vollzugang freigeschaltet!",
      unlockedDesc: "Genieße das Streaming + entdecke weitere Tracks",
      moreMusic: "🎵 Mehr SYNTETIK Musik entdecken",
      visitSite: "Besuche SYNTETIK World",
      premium: "✨ Sofortiger Zugang • Kein Spam • Premium-Qualität"
    },
    fr: {
      unlockTitle: "🎵 Écouter la chanson complète",
      unlockDesc: "Entrez votre email pour débloquer le streaming haute qualité + recevoir les notifications des nouvelles sorties SYNTETIK",
      buttonText: "🎵 DÉBLOQUER LA CHANSON",
      unlockedTitle: "✅ Accès complet débloqué !",
      unlockedDesc: "Profitez du streaming + découvrez plus de titres",
      moreMusic: "🎵 Découvrir Plus de Musique SYNTETIK",
      visitSite: "Visitez SYNTETIK World",
      premium: "✨ Accès instantané • Pas de spam • Qualité premium"
    },
    it: {
      unlockTitle: "🎵 Ascolta la canzone completa",
      unlockDesc: "Inserisci la tua email per sbloccare lo streaming di alta qualità + ricevi notifiche sui nuovi rilasci SYNTETIK",
      buttonText: "🎵 SBLOCCA CANZONE",
      unlockedTitle: "✅ Accesso completo sbloccato!",
      unlockedDesc: "Goditi lo streaming + scopri altri brani",
      moreMusic: "🎵 Scopri Più Musica SYNTETIK",
      visitSite: "Visita SYNTETIK World",
      premium: "✨ Accesso istantaneo • No spam • Qualità premium"
    }
  };

  const currentMessages = messages[language];

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/email-track-capture`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          track_name: 'Its Over',
          source: 'landing_page'
        }),
      });

      if (response.ok) {
        setEmailSubmitted(true);
        
        // Save email to localStorage for future visits to any landing page
        localStorage.setItem('syntetik_user_email', email);
        
        toast({
          title: currentMessages.unlockedTitle,
          description: currentMessages.unlockedDesc,
        });
        
        // Auto-play the song after email submission
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.play().then(() => {
              setIsPlaying(true);
            }).catch(console.error);
          }
        }, 1000);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        setAudioLoading(true);
        setAudioError(false);
        
        audioRef.current.play().then(() => {
          setIsPlaying(true);
          setAudioLoading(false);
          registerPlay();
        }).catch((error) => {
          console.error('Audio playback failed:', error);
          setAudioError(true);
          setAudioLoading(false);
          toast({
            title: "Audio Error",
            description: "Could not play audio. Please try again.",
            variant: "destructive",
          });
        });
      }
    }
  };

  const handleAudioCanPlay = () => {
    setAudioLoading(false);
    setAudioError(false);
  };

  const handleAudioError = () => {
    setAudioLoading(false);
    setAudioError(true);
    toast({
      title: "Audio Loading Error", 
      description: "There was a problem loading the audio file.",
      variant: "destructive",
    });
  };

  const handleAudioLoadStart = () => {
    setAudioLoading(true);
    setAudioError(false);
  };

  const registerPlay = async () => {
    try {
      await fetch(`${API}/tracks/${trackInfo.id}/play`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Failed to register play:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center p-4">
      <div className="max-w-md w-full mx-auto">
        
        {/* Track Artwork & Info */}
        <div className="text-center mb-8">
          <div className="relative mb-6">
            <img 
              src={trackInfo.artwork}
              alt="Its Over Artwork"
              className="w-full aspect-square object-cover rounded-2xl shadow-2xl"
            />
            <div className="absolute inset-0 bg-black/20 rounded-2xl"></div>
          </div>
          
          <h1 className="text-3xl font-light text-white mb-2 tracking-wider">
            {trackInfo.title}
          </h1>
          <p className="text-blue-400 text-lg mb-1">{trackInfo.artist}</p>
          <p className="text-gray-400 text-sm">
            {trackInfo.album} • {trackInfo.year} • {trackInfo.duration}
          </p>
        </div>

        {/* Email Gate or Full Player */}
        {!emailSubmitted ? (
          <Card className="bg-black/40 border-white/20 backdrop-blur-sm p-6">
            <div className="text-center mb-6">
              <h2 className="text-xl text-white font-light mb-3">
                {currentMessages.unlockTitle}
              </h2>
              <p className="text-gray-300 text-sm">
                {currentMessages.unlockDesc}
              </p>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none transition-colors"
              />
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors duration-300"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Unlocking...</span>
                  </div>
                ) : (
                  currentMessages.buttonText
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <p className="text-xs text-gray-400">
                {currentMessages.premium}
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Full Player for the specific track */}
            <Card className="bg-black/40 border-white/20 backdrop-blur-sm p-6">
              <div className="text-center mb-4">
                <p className="text-green-400 text-sm mb-2">{currentMessages.unlockedTitle}</p>
                <h3 className="text-white text-lg">Now Playing: {trackInfo.title}</h3>
              </div>

              <div className="flex items-center justify-center space-x-4 mb-4">
                <Button
                  onClick={handlePlayPause}
                  disabled={audioLoading}
                  className="bg-blue-600 hover:bg-blue-700 w-16 h-16 rounded-full text-2xl disabled:opacity-50"
                >
                  {audioLoading ? (
                    <div className="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full"></div>
                  ) : isPlaying ? '⏸️' : '▶️'}
                </Button>
              </div>

              {audioError && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-4">
                  <p className="text-red-300 text-sm text-center">
                    ⚠️ Audio loading failed. Please check your connection and try again.
                  </p>
                </div>
              )}

              <audio
                ref={audioRef}
                src={trackInfo.audioUrl}
                preload="metadata"
                onCanPlay={handleAudioCanPlay}
                onError={handleAudioError}
                onLoadStart={handleAudioLoadStart}
                onEnded={() => setIsPlaying(false)}
                onPause={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
                className="w-full mt-4"
                controls
              />
            </Card>

            {/* More Music CTA */}
            <Card className="bg-black/40 border-white/20 backdrop-blur-sm p-6">
              <div className="text-center">
                <h3 className="text-white text-lg mb-4">{currentMessages.moreMusic}</h3>
                <p className="text-gray-300 text-sm mb-6">
                  {language === 'es' ? 'Si quieres escuchar más, haz clic aquí' :
                   language === 'pt' ? 'Se você quer ouvir mais, clique aqui' :
                   language === 'de' ? 'Wenn Sie mehr hören möchten, klicken Sie hier' :
                   language === 'fr' ? 'Si vous voulez écouter plus, cliquez ici' :
                   language === 'it' ? 'Se vuoi ascoltare di più, clicca qui' :
                   'If you want to listen to more, click here'}
                </p>
                
                <a
                  href="/#music"
                  className="inline-block w-full"
                  onClick={(e) => {
                    window.location.href = '/#music';
                    setTimeout(() => {
                      document.getElementById('music')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }}
                >
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105">
                    🌐 {currentMessages.visitSite}
                  </Button>
                </a>
              </div>
            </Card>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-400 text-xs">
            © 2025 SYNTETIK World. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ItsOverLanding;